import { useState } from "react";
import { api, formatApiError } from "@/lib/pfc";
import { toast } from "sonner";
import { Send, CheckCircle2 } from "lucide-react";

export default function NewsletterSignup({ source = "footer", variant = "default" }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [devUrl, setDevUrl] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter/subscribe", { email, source });
      if (data.status === "already_confirmed") {
        toast.success("You're already on the list.");
      } else {
        toast.success("Check your email to confirm.");
      }
      setSubmitted(true);
      // Build dev confirm URL from current origin (so it always points to a reachable host)
      if (data._dev_confirm_path) setDevUrl(window.location.origin + data._dev_confirm_path);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const inline = variant === "inline";

  if (submitted) {
    return (
      <div className={`${inline ? "" : "pfc-card p-6"}`} data-testid={`newsletter-confirmed-${source}`}>
        <div className="flex items-start gap-3">
          <CheckCircle2 className="text-pfc-gold shrink-0 mt-1" size={20} />
          <div>
            <div className="font-sub uppercase tracking-[0.15em] text-white text-sm">Check your inbox</div>
            <p className="text-white/60 text-sm mt-1 font-body">We’ve sent a confirmation link to <strong className="text-white/80">{email}</strong>. Click it to lock in your subscription.</p>
            {devUrl && (
              <p className="mt-3 text-xs font-body">
                <span className="label text-pfc-red">MOCKED EMAIL · DEV ONLY</span><br />
                <a href={devUrl} className="text-pfc-gold underline break-all" data-testid={`newsletter-dev-link-${source}`}>{devUrl}</a>
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className={inline ? "flex flex-col sm:flex-row gap-2" : "space-y-3"} data-testid={`newsletter-form-${source}`}>
      <div className="flex-1">
        <input
          required
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@inbox.com"
          className="w-full bg-ink-700 border border-white/10 text-white px-4 py-3 font-body focus:border-pfc-gold focus:outline-none focus:ring-1 focus:ring-pfc-gold"
          data-testid={`newsletter-email-${source}`}
        />
      </div>
      <button
        type="submit"
        disabled={loading}
        className="font-sub uppercase tracking-[0.2em] text-xs px-6 py-3 bg-pfc-gold text-ink-900 hover:bg-pfc-gold-bright transition disabled:opacity-50 flex items-center justify-center gap-2"
        data-testid={`newsletter-submit-${source}`}
      >
        <Send size={14} />
        {loading ? "Subscribing…" : "Subscribe"}
      </button>
    </form>
  );
}
